/**
 * 員工履歷（管理端·行政中心）· func_code = ee_hr_resume。
 * 列表（本租戶全員·含未建立）→ 點人進詳情（ResumeTabs·基本資料 / 學歷 / 經歷 / 證照 / 變更歷史）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Link, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import ResumeTabs, { ResumeTabsHandlers } from '../../components/hr/ResumeTabs';
import { usePermission } from '../../hooks/usePermission';
import { useMessage } from '../../contexts/MessageContext';
import {
  createDetail, deleteDetail, getHistory, getProfile, listProfiles, revealId, updateDetail,
  upsertProfile,
} from '../../services/eeHrResumeService';
import { HrProfile, ProfileListItem } from '../../types/eeHrResume';

const FUNC_CODE = 'ee_hr_resume';

/** 撥號字串：去除空白與分隔符（供 tel:）。 */
const telOf = (v?: string | null): string => String(v || '').replace(/[^\d+]/g, '');

const EeHrResumePage: React.FC = () => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`hrResume.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showApiError } = useMessage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));   // §12 手機寬度改卡片

  const [rows, setRows] = useState<ProfileListItem[]>([]);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [profile, setProfile] = useState<HrProfile | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProfiles({
        search: search || undefined, active: activeOnly ? true : undefined,
      });
      setRows(data);
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [search, activeOnly, showApiError]);

  const loadProfile = useCallback(async (userId: number) => {
    try {
      setProfile(await getProfile(userId));
      setSelected(userId);
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => {
    if (!permLoading && hasPermission(FUNC_CODE, 'read')) loadList();
  }, [permLoading, hasPermission, loadList]);

  if (permLoading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }
  if (!hasPermission(FUNC_CODE, 'read')) {
    return <Box p={3}><Alert severity="warning">{t('common.noPermission', '無此功能存取權限')}</Alert></Box>;
  }

  const reload = () => selected && loadProfile(selected);
  const handlers: ResumeTabsHandlers | null = selected ? {
    saveProfile: (u) => upsertProfile(selected, u),
    reveal: () => revealId(selected),
    loadHistory: (offset, limit) => getHistory(selected, { offset, limit }),
    createDetail: (section, data) => createDetail(selected, section, data),
    updateDetail: (section, id, data) => updateDetail(selected, section, id, data),
    deleteDetail: (section, id) => deleteDetail(selected, section, id),
  } : null;

  return (
    <Box sx={{ p: 2 }}>
      <FunctionPageHeader funcCode={FUNC_CODE} />

      {selected && profile && handlers ? (
        <Box sx={{ mt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => { setSelected(null); setProfile(null); }}>
            {tt('backToList', '返回列表')}
          </Button>
          <Typography variant="h6" sx={{ my: 1 }}>
            {profile.account.username}（{profile.account.account}）
          </Typography>
          <ResumeTabs profile={profile} mode="admin" onReload={reload} handlers={handlers} />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label={tt('searchPH', '姓名 / 帳號 / 身分證末 4 碼')}
              value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') loadList(); }} />
            <Button variant="outlined" onClick={loadList}>{tt('search', '搜尋')}</Button>
            <FormControlLabel control={<Switch checked={activeOnly}
              onChange={e => setActiveOnly(e.target.checked)} />} label={tt('activeOnly', '只看啟用')} />
          </Stack>
          {loading ? <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
            : rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {tt('noEmployees', '查無員工')}
              </Typography>
            ) : isMobile ? (
              // 手機寬度：一張一張的卡片（姓名、部門、手機）
              <Stack spacing={1}>
                {rows.map(r => (
                  <Paper key={r.user_id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">{r.username}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[r.department, r.job_title].filter(Boolean).join(' · ') || r.account}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {telOf(r.phone)
                            ? <Link href={`tel:${telOf(r.phone)}`} underline="hover" variant="body2">{r.phone}</Link>
                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                        </Box>
                        <Box sx={{ mt: 0.5 }}>
                          {!r.has_profile && <Chip size="small" label={tt('notCreated', '未建立')} />}
                          {!r.is_active && <Chip size="small" label={tt('inactive', '停用')} sx={{ ml: 0.5 }} />}
                        </Box>
                      </Box>
                      <Button size="small" onClick={() => loadProfile(r.user_id)}>{tt('open', '檢視')}</Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tt('name', '姓名')}</TableCell>
                    <TableCell>{tt('accountCode', '帳號')}</TableCell>
                    <TableCell>{tt('dept', '部門')}</TableCell>
                    <TableCell>{tt('jobTitle', '職稱')}</TableCell>
                    <TableCell>{tt('phone', '手機')}</TableCell>
                    <TableCell>{tt('idLast4', '身分證末4')}</TableCell>
                    <TableCell>{tt('statusCol', '狀態')}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.user_id} hover>
                      <TableCell>{r.username}</TableCell>
                      <TableCell>{r.account}</TableCell>
                      <TableCell>{r.department || ''}</TableCell>
                      <TableCell>{r.job_title || ''}</TableCell>
                      <TableCell>
                        {telOf(r.phone)
                          ? <Link href={`tel:${telOf(r.phone)}`} underline="hover">{r.phone}</Link>
                          : ''}
                      </TableCell>
                      <TableCell>{r.id_number_last4 || ''}</TableCell>
                      <TableCell>
                        {!r.has_profile && <Chip size="small" label={tt('notCreated', '未建立')} />}
                        {!r.is_active && <Chip size="small" color="default" label={tt('inactive', '停用')} sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => loadProfile(r.user_id)}>{tt('open', '檢視')}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </Box>
      )}
    </Box>
  );
};

export default EeHrResumePage;
